import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router';
import { Toaster } from 'sonner';
import SignUp from './components/auth/SignUp';
import SignIn from './components/auth/SignIn';
import EmailVerification from './components/auth/EmailVerification';
import AccountSettings from './components/auth/AccountSettings';
import PasswordReset from './components/auth/PasswordReset';
import PasswordResetConfirm from './components/auth/PasswordResetConfirm';
import Dashboard from './pages/Dashboard';
import ApiKeys from './pages/ApiKeys';
import Billing from './pages/Billing';
import Analyses from './pages/Analyses';
import AnalysisDetail from './pages/AnalysisDetail';
import Uploads from './pages/Uploads';
import EvaluationCriteria from './pages/EvaluationCriteria';
import HowWeEvaluate from './pages/HowWeEvaluate';
import LandingPage from './pages/LandingPage';
import ComplyLandingPage from './pages/landing/product/ComplyLandingPage';
import QualityLandingPage from './pages/landing/product/QualityLandingPage';
import Pricing from './pages/Pricing';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import About from './pages/About';
import Contact from './pages/Contact';
import Support from './pages/Support';
import AdminRoute from './components/admin/AdminRoute';
import AdminLayout from './components/admin/AdminLayout';
import AdminOverview from './pages/admin/AdminOverview';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminSystem from './pages/admin/AdminSystem';
import AdminRevenue from './pages/admin/AdminRevenue';
import AdminAnalyses from './pages/admin/AdminAnalyses';
import AdminUserAnalyses from './pages/admin/AdminUserAnalyses';
import AdminUserAnalysisDetail from './pages/admin/AdminUserAnalysisDetail';
import AdminRates from './pages/admin/AdminRates';
import DevBanner from './components/DevBanner';
import authService from './services/auth.service';
import './i18n/config';

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  return isAuthenticated ? children : <Navigate to="/signin" replace />;
};

const PublicRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <Router>
      <Toaster
        position="top-right"
        expand={true}
        richColors
        closeButton
        duration={5000}
      />
      <DevBanner />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/compliance" element={<ComplyLandingPage />} />
        <Route path="/quality" element={<QualityLandingPage />} />
        <Route path="/how-we-evaluate" element={<HowWeEvaluate />} />

          <Route
            path="/signup"
            element={
              <PublicRoute>
                <SignUp />
              </PublicRoute>
            }
          />

          <Route
            path="/signin"
            element={
              <PublicRoute>
                <SignIn />
              </PublicRoute>
            }
          />

          <Route
            path="/verify-email"
            element={<EmailVerification />}
          />

          <Route
            path="/reset-password"
            element={<PasswordReset />}
          />

          <Route
            path="/reset-password-confirmation"
            element={<PasswordResetConfirm />}
          />

          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />

          <Route
            path="/account-settings"
            element={
              <PrivateRoute>
                <AccountSettings />
              </PrivateRoute>
            }
          />

          <Route
            path="/api-keys"
            element={
              <PrivateRoute>
                <ApiKeys />
              </PrivateRoute>
            }
          />

          <Route
            path="/billing"
            element={
              <PrivateRoute>
                <Billing />
              </PrivateRoute>
            }
          />

          <Route
            path="/analyses"
            element={
              <PrivateRoute>
                <Analyses />
              </PrivateRoute>
            }
          />

          <Route
            path="/analyses/:id"
            element={
              <PrivateRoute>
                <AnalysisDetail />
              </PrivateRoute>
            }
          />

          <Route
            path="/uploads"
            element={
              <PrivateRoute>
                <Uploads />
              </PrivateRoute>
            }
          />

          <Route
            path="/policy"
            element={
              <PrivateRoute>
                <EvaluationCriteria />
              </PrivateRoute>
            }
          />

          <Route path="/pricing" element={<Pricing />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/support" element={<Support />} />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:id" element={<AdminUserDetail />} />
            <Route path="users/:id/analyses" element={<AdminUserAnalyses />} />
            <Route path="users/:id/analyses/:analysisId" element={<AdminUserAnalysisDetail />} />
            <Route path="analyses" element={<AdminAnalyses />} />
            <Route path="system" element={<AdminSystem />} />
            <Route path="revenue" element={<AdminRevenue />} />
            <Route path="rates" element={<AdminRates />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    </Router>
  );
}

export default App;
